/**
 * Shared payload-display primitives, extracted out of `FieldTiles.tsx` (V1)
 * and `shell-next/rail/FactModule.tsx` (V2)'s byte-identical copies (cleanup
 * item b, rail-glaze-parity QA round). U10's brief originally had
 * `FactModule.tsx` COPY these rather than import them ("FieldTiles stays
 * untouched, this is a fresh V2-only component") — this file is the shared
 * home that brief anticipated once a second consumer existed, without
 * forcing either component's own JSX (`NestedRow`/`NestedTile`,
 * `LeafRow`/`LeafTile`) to merge; those stay separate, each matching its own
 * design system (V1 bordered tiles, V2 Glaze `Module` rows).
 *
 * NOT here: `tileIcon` (FieldTiles-only — V2's `FactModule` has no icon
 * column at all).
 */

export const URL_PATTERN = /^https?:\/\//;

/** A multi-line or long string reads better stacked (label above, value full-width). */
export const LONG_VALUE_LENGTH = 80;

export function isLongText(value: string): boolean {
  return value.length > LONG_VALUE_LENGTH || value.includes('\n');
}

export function isNested(value: unknown): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === 'object';
}

export function nestedEntries(value: Record<string, unknown> | unknown[]): [string, unknown][] {
  return Array.isArray(value) ? value.map((v, i) => [String(i), v] as [string, unknown]) : Object.entries(value);
}

export function copyText(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

/**
 * Stringify a leaf value for display. Narrowed to string/number/boolean
 * before falling back to `JSON.stringify` (never a bare `String(value)` on
 * an `unknown` — oxlint's `no-base-to-string` correctly flags that as
 * unsafe: an arbitrary object would stringify to the useless
 * `"[object Object]"`).
 */
export function displayValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value) ?? '';
}
